import React, {  useState, useCallback } from "react";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "../../tailwind.config.js";
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	Tooltip,
} from "recharts";
import { formatValue } from "../Utils.js";
import moment from "moment";
import dayjs from "dayjs";

const colors = resolveConfig(tailwindConfig).theme.colors;

function determineDateRange(data) {
	if (data !== null && data !== undefined && data.length > 0) {
		let start = data[0].start_time;
		let end = data[data.length - 1].end_time;
		
		return { start, end };
	}
}

function sortData(data) {
	if (data !== null && data !== undefined && data.length > 0) {
		return data.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
	}
}

export const UtilizationChartComponent = (props) => {
	const { data, title,   measure, width,   format = "percent", color, endDate, syncID } = props;

	const [showTooltip, setShowTooltip] = useState(false);

	const dateRange = determineDateRange(data);
	const startDate = moment(dateRange.start).format("dddd, MMMM D")
	const maxDate = moment(endDate).format("dddd, MMMM D")

	const strokeColor = color === "blue" ? "hsl(var(--chart-1))" : "hsl(var(--chart-teal))";

	// Handlers to control tooltip visibility
	const handleMouseEnter = useCallback(() => setShowTooltip(true), []);
	const handleMouseLeave = useCallback(() => setShowTooltip(false), []);
	const handleMouseMove = useCallback(() => setShowTooltip(true), []);

	return (
		<div className="bg-card rounded-card border border-card-bd py-2 pr-2 mt-0">
			<div className="text-text text-xl font-semibold mt-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {title}
            </div>
			<div className="mb-4">
				<div className="flex flex-row justify-between">
					<div className="flex-1">
						<div className="text-text-3 text-sm">
							{startDate} - {maxDate}
						</div>
					</div>
				</div>
			</div>
			<AreaChart
				id={title}
				width={width ? width : 450}
				height={240}
				data={data}
				syncId={syncID}
				margin={{
					top: 30,
					left: 0,
					bottom: 20,
                    right: 10,
				}}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onMouseMove={handleMouseMove}
			>
				<defs>
					<linearGradient id="blue" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.8}/>
						<stop offset="95%" stopColor={colors.white} stopOpacity={0}/>
					</linearGradient>
					<linearGradient id="green" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="hsl(var(--chart-teal))" stopOpacity={0.8}/>
						<stop offset="95%" stopColor={colors.white} stopOpacity={0}/>
					</linearGradient>
				</defs>

				{/* <CartesianGrid strokeDasharray="3 3" /> */}
				<XAxis
					dataKey="start_time"
					tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }}
					stroke="hsl(var(--chart-axis))"
					tickFormatter={(value) => formatXAxis(value, format)}
				/>
				<YAxis
					tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }}
					stroke="hsl(var(--chart-axis))"
					tickFormatter={(value) => formatYAxis(value, format)}
					// domain={['auto', 'auto']}
					// domain={[min-0.05, max+0.05]}

				/>
				<Tooltip
					content={showTooltip ? (value, format)=>renderTooltip(value, format) : () => null}
					cursor={{ fill: "hsl(var(--chart-grid))" }}
					wrapperStyle={{ top: '+225px', zIndex: 10000 }}
				/>
				{/* <ReferenceLine y={min} stroke="red" /> */}
				{/* <ReferenceLine y={max} stroke="red" /> */}

				<Area
					data={sortData(data)}
					// width="200"
					// height="20"
					// cx="50%"
					// cy="50%"
					// stroke=""
					// labelLine={false}
					// outerRadius={10}
					// fill="#8884d8"
					fillOpacity={1} 
					fill={color === "blue" ? "url(#blue)" : "url(#green)"}
					type="monotone"
					dot={false}
					stroke={strokeColor}
					strokeWidth={2}
					// dot={<CustomizedDot />}
					dataKey={measure}
					isAnimationActive={false}
					>
				</Area>
			</AreaChart>
		</div>
	);
};

function formatYAxis(value, format) {
	return formatValue(value, format, false);
}

function formatXAxis(value, format) {
	// return formatValue(value, format, false);
	return dayjs(value).format("DD-MM-YYYY");
}




const renderTooltip = (props) => {
	const { active, payload } = props;
	let title ="";

	if (payload && payload.length > 0) {
		switch (payload[0].name) {
			case "start_time":
				title = "Time";
				break;
			case "cpu_utilization":
				title = "Average CPU Utilization";
				break;
			case "memory_utilization":
				title = "Average Memory Utilization";
				break;
			case "storage_utilization":
				title = "Average Storage Utilization";
				break;
			case "cost":
				title = "Cost";
				break;
			default:
				title = "Utilization";
				break;
		}
	}

	if (active && payload && payload.length) {
		return (
			<div className="custom-tooltip bg-surface rounded-card border border-card-bd shadow-float p-1 minw-64 ml-8 py-4 px-6">
				<div className="flex flex-row mb-2">
					<p className="flex-1 label font-semibold mr-4">
						{title}
					</p>
					<p className="text-right label font-semibold">
						{payload[0].name === "cost" ? 
							`${formatValue(payload[0].value, "currency", false, 2)}` :
							`${formatValue(payload[0].value, "percent", false, 1)}`
						}
					</p>
				</div>
				<div>
					{payload[0].name === "cost" && (
						<div className="flex-1 mr-4">
							{`${moment(payload[0].payload.start_time).format("dddd, MMMM D")}`}				
						</div>
					)}
					{payload[0].name !== "cost" && (
						<>
						<div className="flex-1 mr-4">
							{`${moment(payload[0].payload.start_time).format("dddd, MMMM D HH:mm:ss")}`}				
						</div>

						<div className="flex-1 mr-4">
							{`${moment(payload[0].payload.end_time).format("dddd, MMMM D HH:mm:ss")}`}
						</div>
						</>
					)}
				</div>
			</div>
		);
	}

	return null;
};


